import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "highkey_cart_v3";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Each cart line is keyed by (product_id, batch_mode, package_key)
  const lineKey = (productId, batchMode, packageKey) =>
    `${productId}::${batchMode}::${packageKey || "_"}`;

  /**
   * Add a line. `options` = { batchMode, packageKey, packageLabel, unitPrice, minQty }
   */
  const add = (product, options, quantity = 1) => {
    const batchMode = options?.batchMode || "small";
    const packageKey = options?.packageKey || null;
    const packageLabel = options?.packageLabel || null;
    const unitPrice = options?.unitPrice;
    const minQty = options?.minQty || 1;
    const qty = Math.max(minQty, quantity);
    setItems((prev) => {
      const ex = prev.find(
        (i) => lineKey(i.product.id, i.batch_mode, i.package_key) === lineKey(product.id, batchMode, packageKey)
      );
      if (ex)
        return prev.map((i) =>
          lineKey(i.product.id, i.batch_mode, i.package_key) === lineKey(product.id, batchMode, packageKey)
            ? { ...i, quantity: i.quantity + qty }
            : i
        );
      return [
        ...prev,
        {
          product,
          batch_mode: batchMode,
          package_key: packageKey,
          package_label: packageLabel,
          unit_price: unitPrice,
          min_qty: minQty,
          quantity: qty,
        },
      ];
    });
    setOpen(true);
  };

  const remove = (productId, batchMode, packageKey) =>
    setItems((prev) =>
      prev.filter((i) => lineKey(i.product.id, i.batch_mode, i.package_key) !== lineKey(productId, batchMode, packageKey))
    );

  const updateQty = (productId, batchMode, packageKey, quantity) =>
    setItems((prev) =>
      prev
        .map((i) =>
          lineKey(i.product.id, i.batch_mode, i.package_key) === lineKey(productId, batchMode, packageKey)
            ? { ...i, quantity }
            : i
        )
        .filter((i) => i.quantity > 0)
    );

  const clear = () => setItems([]);

  const priceOf = (i) => {
    if (i.unit_price != null) return i.unit_price;
    return i.batch_mode === "wholesale" && i.product.wholesale_price != null
      ? i.product.wholesale_price
      : i.product.price;
  };

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + priceOf(i) * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, count, subtotal, add, remove, updateQty, clear, open, setOpen, priceOf, lineKey }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
